package co.maytes.standupbot.dashboard.features.templateslist

import co.maytes.standupbot.dashboard.features.templates.TemplatesStore
import co.maytes.standupbot.dashboard.lib.api.Result
import com.kttipay.mvi.api.Container
import com.kttipay.mvi.api.StoreScope
import com.kttipay.mvi.dsl.store
import kotlinx.coroutines.CoroutineScope

class TemplatesListContainer(
    scope: CoroutineScope,
    private val templates: TemplatesStore,
) : Container<TemplatesListContract.State, TemplatesListContract.Intent, TemplatesListContract.Action> {

    override val store = scope.store<
            TemplatesListContract.State,
            TemplatesListContract.Intent,
            TemplatesListContract.Action,
            >(initialState = TemplatesListContract.State()) {
        configure(name = "TemplatesList")
        logging()
        reduce { intent ->
            when (intent) {
                is TemplatesListContract.Intent.Load -> {
                    updateState { copy(workspaceId = intent.workspaceId) }
                    load()
                }
                TemplatesListContract.Intent.OpenNew ->
                    action(TemplatesListContract.Action.Navigate("/app/templates/new"))
                TemplatesListContract.Intent.OpenDuplicate ->
                    action(TemplatesListContract.Action.Navigate("/app/templates/new?from=default"))
                is TemplatesListContract.Intent.OpenDetail ->
                    action(TemplatesListContract.Action.Navigate("/app/templates/${intent.id}"))
                is TemplatesListContract.Intent.RequestDelete ->
                    updateState { copy(deleteTargetId = intent.id) }
                TemplatesListContract.Intent.CancelDelete ->
                    updateState { copy(deleteTargetId = null) }
                TemplatesListContract.Intent.ConfirmDelete -> confirmDelete()
            }
        }
    }

    private suspend fun StoreScope<TemplatesListContract.State, TemplatesListContract.Action>.load() {
        val ws = currentState.workspaceId
        if (ws.isBlank()) return
        updateState { copy(status = TemplatesListContract.Status.Loading) }
        when (val res = templates.refresh(ws)) {
            is Result.Ok ->
                updateState {
                    copy(items = res.value, status = TemplatesListContract.Status.Ready)
                }
            is Result.Err ->
                updateState {
                    copy(status = TemplatesListContract.Status.Error(res.error.displayMessage))
                }
        }
    }

    private suspend fun StoreScope<TemplatesListContract.State, TemplatesListContract.Action>.confirmDelete() {
        val state = currentState
        val id = state.deleteTargetId ?: return
        if (state.workspaceId.isBlank()) return
        when (val res = templates.delete(state.workspaceId, id)) {
            is Result.Ok -> {
                updateState { copy(deleteTargetId = null) }
                action(TemplatesListContract.Action.Toast("Template deleted."))
                load()
            }
            is Result.Err -> {
                updateState { copy(deleteTargetId = null) }
                action(
                    TemplatesListContract.Action.Toast(
                        "Delete failed: ${res.error.displayMessage}",
                        error = true,
                    ),
                )
            }
        }
    }
}
